import type { AxiosResponse } from 'axios';

import { API_URL } from '@/config/env';
import { conformanceApi } from '@/lib/api';

import type {
  AppRole,
  AssignRoleRequest,
  CreateRoleRequest,
  EntraUser,
  RoleAssignment,
  RoleHolder,
  UpdateRoleRequest,
} from './access.types';

/**
 * User-access service (Entra app-role assignment).
 *
 * Calls the Conformance UserManagementController @ `api/v4.0/system/users`:
 *   GET    /system/users/roles                                  -> AppRole[]
 *   GET    /system/users                                        -> RoleHolder[]
 *   GET    /system/users/search?q=                              -> EntraUser[]
 *   POST   /system/users/assignments                            -> RoleAssignment
 *   DELETE /system/users/assignments/{assignmentId}?roleValue=  -> 204
 */

// `/system` routes hang off the API root, not the `/orr` segment (same trick
// used by getUserRoleAccess / getSystemCheckpointConfigurations).
const USERS_BASE = `${API_URL.replace('/orr', '')}/system/users`;

export const getAssignableRoles = (): Promise<AxiosResponse<AppRole[]>> =>
  conformanceApi.get<AppRole[]>(`${USERS_BASE}/roles`);

export const getRoleHolders = (): Promise<AxiosResponse<RoleHolder[]>> =>
  conformanceApi.get<RoleHolder[]>(USERS_BASE);

export const searchEntraUsers = (
  q: string,
): Promise<AxiosResponse<EntraUser[]>> =>
  conformanceApi.get<EntraUser[]>(`${USERS_BASE}/search`, { params: { q } });

export const assignRole = (
  body: AssignRoleRequest,
): Promise<AxiosResponse<RoleAssignment>> =>
  conformanceApi.post<RoleAssignment>(`${USERS_BASE}/assignments`, body);

export const revokeRole = (
  assignmentId: string,
  roleValue: string,
): Promise<AxiosResponse<void>> =>
  conformanceApi.delete<void>(
    `${USERS_BASE}/assignments/${encodeURIComponent(assignmentId)}`,
    { params: { roleValue } },
  );

// --- App-role definition management (POST/PUT/DELETE /system/users/roles) ---

export const createRole = (
  body: CreateRoleRequest,
): Promise<AxiosResponse<AppRole>> =>
  conformanceApi.post<AppRole>(`${USERS_BASE}/roles`, body);

export const updateRole = (
  appRoleId: string,
  body: UpdateRoleRequest,
): Promise<AxiosResponse<AppRole>> =>
  conformanceApi.put<AppRole>(
    `${USERS_BASE}/roles/${encodeURIComponent(appRoleId)}`,
    body,
  );

export const deleteRole = (
  appRoleId: string,
): Promise<AxiosResponse<void>> =>
  conformanceApi.delete<void>(
    `${USERS_BASE}/roles/${encodeURIComponent(appRoleId)}`,
  );
